import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../lib/supabase';
import { fetchWithAuthRefresh } from '../lib/supabaseAuthRefresh';
import ChinaConsultPopup from '../components/ChinaConsultPopup';
import ContactForm from '../components/ContactForm';
import Footer from '../components/Footer';
import Header from '../components/Header';
import PageHeader from '../components/PageHeader';

// --- Стилизация ---
const Container = styled.section`
  background-color: #ededed;
  min-height: 100vh;
  padding: 0 0 60px 0;

  @media (max-width: 640px) {
    padding: 0 0 40px 0;
  }
`;

// Кнопка назад
const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid #e8e8e8;
  padding: 12px 24px;
  border-radius: 40px;
  font-size: 15px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  margin-bottom: 24px;
  margin-top: 24px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
    transform: translateX(-4px);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 13px;
    margin-bottom: 16px;
  }
`;

const HeroSection = styled.div`
  position: relative;
  width: 100%;
  max-width: 1140px;
  margin: 0 auto 40px;
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0 20px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    padding: 0 16px;
    margin-bottom: 20px;
  }
`;

const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 460px;
  background-color: #1a1e24;
  overflow: hidden;
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.85;
    transition: transform 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(to top, rgba(0,0,0,0.5), transparent);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    height: 350px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    height: 250px;
    border-radius: 16px;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  padding: 0 40px;
  color: white;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 24px;
    bottom: 24px;
  }

  @media (max-width: 480px) {
    bottom: 16px;
    padding: 0 20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 42px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin-bottom: 12px;
  text-shadow: 0 4px 12px rgba(0,0,0,0.3);
  font-family: 'Inter', 'Geologica', sans-serif;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  opacity: 0.9;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
  font-weight: 400;
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 40px;

  @media (max-width: 1024px) {
    gap: 24px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0; // Важно для предотвращения переполнения на мобильных

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const RightColumn = styled.div`
  position: relative;

  @media (max-width: 900px) {
    position: static;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  border: 1px solid #f0f0f0;
  transition: box-shadow 0.2s ease;
  overflow: hidden; // Предотвращает выход содержимого за границы

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const CardTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${props => props.$color || '#111'};
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${props => props.$borderColor || '#eee'};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  
  span {
    background: #f5f5f5;
    padding: 2px 10px;
    border-radius: 30px;
    font-size: 14px;
    font-weight: 500;
    color: #555;
    margin-left: 0;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 16px;
  }
`;

// Карточка с ценой и кнопкой (Right Column)
const PriceCard = styled(Card)`
  position: sticky;
  top: 24px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  padding: 28px 24px;

  @media (max-width: 900px) {
    position: static;
  }

  @media (max-width: 768px) {
    padding: 24px 20px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
`;

const PriceHeader = styled.div`
  margin-bottom: 24px;

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const PriceLabel = styled.div`
  font-size: 15px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const PriceValue = styled.div`
  font-size: 44px;
  font-weight: 700;
  color: #111;
  letter-spacing: -0.02em;
  line-height: 1;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 38px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const PriceNote = styled.div`
  font-size: 13px;
  color: #888;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Divider = styled.hr`
  margin: 24px 0;
  border: none;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 480px) {
    margin: 20px 0;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 16px;
  
  .label {
    font-size: 15px;
    color: #666;
    font-weight: 400;
  }
  
  .value {
    font-size: 16px;
    font-weight: 500;
    color: #111;
    text-align: right;
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;
    
    .label {
      font-size: 14px;
    }
    
    .value {
      font-size: 14px;
    }
  }
`;

// Removed: DeadlineBadge (no longer used)

// Оранжевая кнопка бронирования
const BookButton = styled.button`
  width: 100%;
  background: linear-gradient(0.75turn, #FF9966, #FF5E62);
  color: white;
  border: none;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;
  letter-spacing: 0.3px;
  box-shadow: 0px 5px 15px 0px rgba(255, 153, 102, 0.5);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 8px 20px 0px rgba(255, 153, 102, 0.6);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 14px 20px;
    font-size: 15px;
  }

  @media (max-width: 480px) {
    padding: 14px 18px;
    font-size: 14px;
    margin-top: 12px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const InfoItem = styled.div`
  min-width: 0; // Предотвращает переполнение
  
  .label {
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #888;
    margin-bottom: 8px;
  }
  
  .value {
    font-size: 18px;
    font-weight: 600;
    color: #111;
    line-height: 1.4;
    word-break: break-word; // Перенос длинных слов
  }
  
  .sub {
    font-size: 14px;
    color: #666;
    margin-top: 4px;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    .label {
      font-size: 11px;
    }
    
    .value {
      font-size: 16px;
    }
    
    .sub {
      font-size: 13px;
    }
  }

  @media (max-width: 480px) {
    .value {
      font-size: 15px;
    }
  }
`;

const RouteText = styled.div`
  font-size: 17px;
  line-height: 1.6;
  color: #2c2c2c;
  padding: 16px 0 8px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 8px;
  font-weight: 450;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 0 8px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

// Таблицы
const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 8px 0 16px;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  min-width: 500px;
  
  th {
    text-align: left;
    padding: 16px 12px 12px 0;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #e0e0e0;
    white-space: nowrap;
  }
  
  td {
    padding: 14px 12px 14px 0;
    border-bottom: 1px solid #f0f0f0;
    color: #2c2c2c;
    font-weight: 400;
  }
  
  tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    min-width: 450px;
    
    th {
      padding: 12px 8px 10px 0;
    }
    
    td {
      padding: 10px 8px 10px 0;
    }
  }
`;

// Список (особенности, включено/исключено)
const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  
  li {
    padding-left: 24px;
    position: relative;
    font-size: 15.5px;
    line-height: 1.6;
    color: #333;
    word-break: break-word;
    
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      width: 6px;
      height: 6px;
      background: #FF5E62;
      border-radius: 1px;
    }
  }

  @media (max-width: 768px) {
    gap: 10px;
    
    li {
      font-size: 14.5px;
      padding-left: 20px;
    }
  }

  @media (max-width: 480px) {
    li {
      font-size: 14px;
    }
  }
`;

// Программа по дням
const DayCard = styled.div`
  border-bottom: 1px solid #f0f0f0;
  padding: 18px 0;
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 14px 0;
  }
`;

const DayHeader = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  user-select: none;
  
  .day-num {
    font-size: 14px;
    font-weight: 600;
    color: #666;
    min-width: 60px;
  }
  
  .day-title {
    font-size: 18px;
    font-weight: 600;
    color: #111;
    flex: 1;
    word-break: break-word;
  }
  
  .day-date {
    font-size: 14px;
    color: #888;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 10px;
    
    .day-num {
      min-width: 50px;
      font-size: 13px;
    }
    
    .day-title {
      font-size: 16px;
    }
    
    .day-date {
      font-size: 13px;
      width: 100%;
      margin-left: 58px;
    }
  }

  @media (max-width: 480px) {
    .day-title {
      font-size: 15px;
    }
    
    .day-date {
      margin-left: 50px;
    }
  }
`;

const DayDescription = styled.div`
  padding-left: 76px;
  font-size: 15px;
  line-height: 1.7;
  color: #3a3a3a;
  white-space: pre-wrap;
  padding-bottom: 8px;
  word-break: break-word;
  
  @media (max-width: 768px) {
    padding-left: 58px;
    font-size: 14px;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    padding-left: 0;
    font-size: 13.5px;
  }
`;

// Стили для текста внутри карточек
const DescriptionText = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: #2c2c2c;
  white-space: pre-wrap;
  margin: 0;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const FlightInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const FlightItem = styled.div`
  font-size: 15px;
  word-break: break-word;
  
  strong {
    color: #111;
    margin-right: 12px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const BookingInfo = styled.div`
  p {
    margin-bottom: 12px;
    word-break: break-word;
    
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
`;

// --- Компонент ---
const CruiseDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cruise, setCruise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConsultPopupOpen, setIsConsultPopupOpen] = useState(false);

  useEffect(() => {
    const fetchCruiseData = async () => {
      setLoading(true);
      try {
        const { data, error } = await fetchWithAuthRefresh(async () => {
          return await supabase
            .from('cruises')
            .select('*')
            .eq('slug', slug)
            .eq('status', 'active')
            .single();
        });

        if (error) throw error;
        if (!data) {
          navigate('/cruises');
          return;
        }
        setCruise(data);
      } catch (error) {
        console.error('Error fetching cruise:', error);
        // Если ошибка 401, не навигируем, даем chance на refresh
        if (error.status !== 401) {
          navigate('/cruises');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCruiseData();
    }
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '6rem', fontSize: '18px', color: '#666' }}>Загрузка...</div>;
  }

  if (!cruise) {
    return <div style={{ textAlign: 'center', padding: '6rem', fontSize: '18px', color: '#666' }}>Круиз не найден</div>;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  };

  return (
    <>
      <Container>
        <Header />
        <PageHeader />

        <ContentWrapper>
          {/* Кнопка назад */}
          <BackButton onClick={handleBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Назад к круизам
          </BackButton>
        </ContentWrapper>

        <HeroSection>
          <HeroImageWrapper>
            {cruise.image_url && <img src={cruise.image_url} alt={cruise.title} />}
            <HeroOverlay>
              {cruise.subtitle && <HeroSubtitle>{cruise.subtitle}</HeroSubtitle>}
              <HeroTitle>{cruise.title}</HeroTitle>
            </HeroOverlay>
          </HeroImageWrapper>
        </HeroSection>

        <ContentWrapper>
          <MainGrid>
            <LeftColumn>
              {/* Основные параметры */}
              <Card style={{ padding: '24px 28px' }}>
                <InfoGrid>
                  <InfoItem>
                    <div className="label">Отправление</div>
                    <div className="value">{formatDate(cruise.departure_date)}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Длительность</div>
                    <div className="value">{cruise.duration_days} дня</div>
                    <div className="sub">{cruise.duration_nights} ночей</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Корабль</div>
                    <div className="value">{cruise.ship_name}</div>
                    <div className="sub">{cruise.company_name}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Маршрут</div>
                    <div className="value" style={{ fontSize: '16px' }}>{cruise.route?.split('—')[0] || '—'}</div>
                  </InfoItem>
                </InfoGrid>
                {cruise.route && <RouteText>{cruise.route}</RouteText>}
              </Card>

              {/* Описание */}
              {cruise.description && (
                <Card>
                  <CardTitle $color="#45B7D1" $borderColor="#E5F3FA">Обзор круиза</CardTitle>
                  <DescriptionText>
                    {cruise.description}
                  </DescriptionText>
                </Card>
              )}

              {/* Цены по категориям кают */}
              {cruise.cabin_categories && cruise.cabin_categories.length > 0 && (
                <Card>
                  <CardTitle $color="#FF6B6B" $borderColor="#FFE5E5">Стоимость кают <span>за человека при 2-местном размещении</span></CardTitle>
                  <TableWrapper>
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>Категория</th>
                          <th>Тип каюты</th>
                          <th style={{ textAlign: 'right' }}>Цена</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cruise.cabin_categories.map((cabin, index) => (
                          <tr key={index}>
                            <td style={{ fontWeight: 500 }}>{cabin.category_code || `Категория ${index + 1}`}</td>
                            <td>{cabin.type || '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 500 }}>
                              {cabin.price ? `${Number(cabin.price).toLocaleString('ru-RU')} ₽` : 'По запросу'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </StyledTable>
                  </TableWrapper>
                </Card>
              )}

              {/* Расписание портов */}
              {cruise.ports_schedule && cruise.ports_schedule.length > 0 && (
                <Card>
                  <CardTitle $color="#4ECDC4" $borderColor="#E5F9F7">Расписание по портам</CardTitle>
                  <TableWrapper>
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>День</th>
                          <th>Порт</th>
                          <th>Прибытие</th>
                          <th>Стоянка</th>
                          <th>Отправление</th>
                          <th>Примечание</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cruise.ports_schedule.map((port, index) => (
                          <tr key={index}>
                            <td><strong>День {port.port_number}</strong></td>
                            <td>{port.port_name}</td>
                            <td>{port.arrival || '—'}</td>
                            <td>{port.stay || '—'}</td>
                            <td>{port.departure || '—'}</td>
                            <td>{port.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </StyledTable>
                  </TableWrapper>
                </Card>
              )}

              {/* Программа по дням */}
              {cruise.daily_program && cruise.daily_program.length > 0 && (
                <Card>
                  <CardTitle $color="#9B8DC2" $borderColor="#EDE7F6">Программа по дням</CardTitle>
                  <div>
                    {cruise.daily_program.filter(day => day.day_title || day.day_description).map((day, index) => (
                      <DayCard key={index}>
                        <DayHeader>
                          <span className="day-num">День {day.day_number}</span>
                          <span className="day-title">{day.day_title}</span>
                          {day.day_date && <span className="day-date">{formatShortDate(day.day_date)}</span>}
                        </DayHeader>
                        <DayDescription>
                          {day.day_description}
                        </DayDescription>
                      </DayCard>
                    ))}
                  </div>
                </Card>
              )}

              {/* Особенности */}
              {cruise.highlights && cruise.highlights.filter(h => h.trim()).length > 0 && (
                <Card>
                  <CardTitle $color="#96CEB4" $borderColor="#E8F5E9">Особенности круиза</CardTitle>
                  <FeatureList>
                    {cruise.highlights.filter(h => h.trim()).map((highlight, index) => (
                      <li key={index}>{highlight}</li>
                    ))}
                  </FeatureList>
                </Card>
              )}

              {/* Включено */}
              {cruise.included && cruise.included.filter(i => i.trim()).length > 0 && (
                <Card>
                  <CardTitle $color="#5B9BD5" $borderColor="#E8F0FE">В стоимость включено</CardTitle>
                  <FeatureList>
                    {cruise.included.filter(i => i.trim()).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </FeatureList>
                </Card>
              )}

              {/* Исключено */}
              {cruise.excluded && cruise.excluded.filter(e => e.trim()).length > 0 && (
                <Card>
                  <CardTitle $color="#FF8C8C" $borderColor="#FFE8E8">Оплачивается дополнительно</CardTitle>
                  <FeatureList>
                    {cruise.excluded.filter(e => e.trim()).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </FeatureList>
                </Card>
              )}

              {/* Перелет */}
              {(cruise.flight_departure || cruise.flight_return) && (
                <Card>
                  <CardTitle $color="#87CEEB" $borderColor="#E8F4FD">Информация о перелете</CardTitle>
                  <FlightInfo>
                    {cruise.flight_departure && (
                      <FlightItem><strong>Туда:</strong> {cruise.flight_departure}</FlightItem>
                    )}
                    {cruise.flight_return && (
                      <FlightItem><strong>Обратно:</strong> {cruise.flight_return}</FlightItem>
                    )}
                    {cruise.flight_info && <p style={{ marginTop: '12px', color: '#555' }}>{cruise.flight_info}</p>}
                  </FlightInfo>
                </Card>
              )}

              {/* Условия бронирования */}
              {(cruise.booking_conditions || cruise.deposit_amount || cruise.visa_info) && (
                <Card>
                  <CardTitle $color="#F0A55B" $borderColor="#FFF3E0">Условия бронирования</CardTitle>
                  <BookingInfo>
                    {cruise.booking_conditions && <DescriptionText as="p">{cruise.booking_conditions}</DescriptionText>}
                    {cruise.deposit_amount && <p><strong>Депозит:</strong> {cruise.deposit_amount}</p>}
                    {cruise.final_payment_days && <p><strong>Финальный платеж:</strong> за {cruise.final_payment_days} дней до отправления</p>}
                    {cruise.visa_info && (
                      <div style={{ marginTop: '24px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Визовая информация</h3>
                        <DescriptionText as="p">{cruise.visa_info}</DescriptionText>
                      </div>
                    )}
                  </BookingInfo>
                </Card>
              )}
            </LeftColumn>

            <RightColumn>
              <PriceCard>
                <PriceHeader>
                  <PriceLabel>Стоимость от</PriceLabel>
                  <PriceValue>{cruise.price_from?.toLocaleString('ru-RU')} ₽</PriceValue>
                  <PriceNote>на человека при 2-местном размещении</PriceNote>
                </PriceHeader>

                <Divider />

                <InfoRow>
                  <span className="label">Даты</span>
                  <span className="value">{formatShortDate(cruise.departure_date)} — {cruise.duration_days} дн.</span>
                </InfoRow>
                <InfoRow>
                  <span className="label">Корабль</span>
                  <span className="value">{cruise.ship_name}</span>
                </InfoRow>

                <BookButton onClick={() => setIsConsultPopupOpen(true)}>
                  Забронировать
                </BookButton>
                <p style={{ fontSize: '12px', color: '#999', textAlign: 'center', marginTop: '16px' }}>
                  Бронирование по запросу. Наш менеджер свяжется с вами.
                </p>
              </PriceCard>
            </RightColumn>
          </MainGrid>
        </ContentWrapper>
        <ContactForm />
        <Footer />
      </Container>

      <ChinaConsultPopup 
        isOpen={isConsultPopupOpen} 
        onClose={() => setIsConsultPopupOpen(false)} 
        selectedTour={cruise}
      />
    </>
  );
};

export default CruiseDetailPage;